import type { PlannedSession, SessionType } from "@runlens/shared";
import {
  BoltIcon,
  FireIcon,
  HeartIcon,
  SparklesIcon,
  SunIcon,
  MoonIcon,
} from "@heroicons/react/24/outline";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";

interface SessionCellProps {
  session?: PlannedSession;
  date: Date;
  isToday?: boolean;
  compact?: boolean;
  onClick?: () => void;
}

const sessionTypeConfig: Record<
  SessionType,
  { icon: typeof BoltIcon; label: string; color: string; bgColor: string }
> = {
  easy: {
    icon: SunIcon,
    label: "Easy",
    color: "text-green-600",
    bgColor: "bg-green-50 hover:bg-green-100",
  },
  long: {
    icon: SparklesIcon,
    label: "Long",
    color: "text-blue-600",
    bgColor: "bg-blue-50 hover:bg-blue-100",
  },
  tempo: {
    icon: FireIcon,
    label: "Tempo",
    color: "text-orange-600",
    bgColor: "bg-orange-50 hover:bg-orange-100",
  },
  intervals: {
    icon: BoltIcon,
    label: "Intervals",
    color: "text-red-600",
    bgColor: "bg-red-50 hover:bg-red-100",
  },
  recovery: {
    icon: HeartIcon,
    label: "Recovery",
    color: "text-purple-600",
    bgColor: "bg-purple-50 hover:bg-purple-100",
  },
  rest: {
    icon: MoonIcon,
    label: "Rest",
    color: "text-gray-400",
    bgColor: "bg-gray-50",
  },
};

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hrs}h${mins}m` : `${hrs}h`;
}

function formatDistance(meters: number): string {
  return `${(meters / 1000).toFixed(1)}km`;
}

export function SessionCell({ session, date, isToday, compact, onClick }: SessionCellProps) {
  const dayNum = date.getDate();
  const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

  if (!session) {
    return (
      <div
        className={`${compact ? "h-[4.25rem]" : "h-20"} border border-gray-100 rounded-lg p-1 ${
          isToday ? "ring-2 ring-brand-500 ring-offset-1" : ""
        }`}
      >
        <span className={`text-xs ${isToday ? "text-brand-600 font-semibold" : "text-gray-400"}`}>
          {dayNum}
        </span>
      </div>
    );
  }

  const config = sessionTypeConfig[session.sessionType];
  const Icon = config.icon;

  // Status indicator
  let StatusIcon = null;
  let statusColor = "";
  if (session.status === "completed") {
    StatusIcon = CheckCircleIcon;
    statusColor = "text-green-500";
  } else if (session.status === "missed") {
    StatusIcon = XCircleIcon;
    statusColor = "text-red-500";
  } else if (session.status === "skipped") {
    StatusIcon = ExclamationCircleIcon;
    statusColor = "text-yellow-500";
  }

  // Build tooltip content
  const tooltipParts: string[] = [config.label];
  if (session.targetDuration) {
    tooltipParts.push(formatDuration(session.targetDuration));
  }
  if (session.targetDistance) {
    tooltipParts.push(formatDistance(session.targetDistance));
  }
  if (session.targetZone) {
    tooltipParts.push(`Z${session.targetZone}`);
  }

  if (compact) {
    const details = [
      session.targetDuration ? formatDuration(session.targetDuration) : null,
      session.targetDistance ? formatDistance(session.targetDistance) : null,
    ].filter(Boolean).join(" · ");

    return (
      <button
        onClick={onClick}
        className={`h-[4.25rem] w-full border rounded-lg p-1 transition-colors cursor-pointer ${config.bgColor} ${
          isToday ? "ring-2 ring-brand-500 ring-offset-1" : "border-gray-100"
        } ${isPast && session.status === "planned" ? "opacity-60" : ""}`}
        title={tooltipParts.join(" · ")}
      >
        <div className="flex items-start justify-between">
          <span className={`text-xs leading-none ${isToday ? "text-brand-600 font-semibold" : "text-gray-500"}`}>
            {dayNum}
          </span>
          {StatusIcon && <StatusIcon className={`w-3.5 h-3.5 ${statusColor}`} />}
        </div>
        <div className="flex items-center justify-center gap-1">
          <Icon className={`w-4 h-4 shrink-0 ${config.color}`} />
          <span className={`text-xs font-medium ${config.color} leading-tight`}>
            {config.label}
          </span>
        </div>
        {details && (
          <div className="text-center">
            <span className="text-[10px] text-gray-500 leading-none">{details}</span>
          </div>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`h-20 w-full border rounded-lg p-1 transition-colors cursor-pointer ${config.bgColor} ${
        isToday ? "ring-2 ring-brand-500 ring-offset-1" : "border-gray-100"
      } ${isPast && session.status === "planned" ? "opacity-60" : ""}`}
      title={tooltipParts.join(" · ")}
    >
      <div className="flex items-start justify-between">
        <span className={`text-xs ${isToday ? "text-brand-600 font-semibold" : "text-gray-500"}`}>
          {dayNum}
        </span>
        {StatusIcon && <StatusIcon className={`w-4 h-4 ${statusColor}`} />}
      </div>
      <div className="flex items-center justify-center gap-2">
        <Icon className={`w-7 h-7 ${config.color}`} />
        <div className="flex flex-col items-start">
          <span className={`text-sm font-medium ${config.color} leading-tight`}>
            {config.label}
          </span>
          {(session.targetDuration || session.targetDistance) && (
            <span className="text-xs text-gray-500 leading-tight">
              {session.targetDuration && formatDuration(session.targetDuration)}
              {session.targetDuration && session.targetDistance && " · "}
              {session.targetDistance && formatDistance(session.targetDistance)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
